import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import _findIndex from 'lodash/findIndex';
import _find from 'lodash/find';
import { useClickOutside } from '../../../hooks/useClickOutside';
import { Error } from '../Error';
import '../formV2.less';
import './DropdownV2.less';

const debounce = (func, wait) => {
  let timeout;

  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// https://stackoverflow.com/questions/45408920/plain-javascript-scrollintoview-inside-div
function scrollParentToChild(parent, child) {
  // Where is the parent on page
  const parentRect = parent.getBoundingClientRect();
  // What can you see?
  const parentViewableArea = {
    height: parent.clientHeight,
    width: parent.clientWidth,
  };

  // Where is the child
  const childRect = child.getBoundingClientRect();
  // Is the child viewable?
  const isViewable =
    childRect.top >= parentRect.top && childRect.top <= parentRect.top + parentViewableArea.height;

  // if you can't see the child try to scroll parent
  if (!isViewable) {
    // scroll by offset relative to parent
    // console.log('NOT VIEWABLE');
    parent.scrollTop = childRect.top + parent.scrollTop - parentRect.top; // eslint-disable-line
  }
}

/* eslint-disable */
const uuidv4 = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c == 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};
/* eslint-enable */

/**
 *
 * @param {*} props
 *
 * This listbox follows the WAI-ARIA standards listed in https://www.w3.org/TR/wai-aria-practices/examples/listbox/listbox-collapsible.html for listboxes.
 */
const DropdownV2 = (props) => {
  const {
    onSelect,
    options,
    label,
    placeholder,
    inline,
    errorMessage,
    className,
    inlineLabel,
    nullable,
    nullOptionLabel,
    isRequired,
    id,
    disabled,
    value,
    useOptionStyles,
  } = props;

  const [expanded, setExpanded] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [prevValue, setPrevValue] = useState(null);
  const [search, setSearch] = useState('');
  // Closes dropdown if click outside
  const ref = useRef(null);
  useClickOutside({
    ref,
    onClickOutside: () => {
      setExpanded(false);
      if (selectedOption != null && onSelect) {
        onSelect(selectedOption.value);
      }
    },
    isOpen: expanded,
  });

  function handleOptionSelection(option, shouldExpand) {
    setSelectedOption(option);
    if (onSelect) {
      onSelect(option.value);
    }
    setExpanded(shouldExpand); // toggles the dropdown open and closed
  }

  // Set up debounce on clearing internal search
  const debouncedClear = useCallback(
    debounce(() => setSearch(''), 250),
    []
  );

  useEffect(() => {
    if (!search) return;
    const newSelection = _find(options, (o) => o.label.toLowerCase().includes(search));
    if (newSelection) {
      setSelectedOption(newSelection);
    }
  }, [options, search]);

  // Sets class names for different sections
  const [classNames, setClassNames] = useState('dropdown-v2__container zola-ui v2-render-field');
  useEffect(() => {
    setClassNames(
      cx('dropdown-v2__container zola-ui v2-render-field', className, {
        inline,
        'inline-label': inlineLabel,
      })
    );
  }, [className, inline, inlineLabel]);

  const [selectionBoxClassNames, setSelectionBoxClassNames] = useState(
    'dropdown-v2__selection-box zola-ui v2-render-field'
  );

  // updates classnames based off various state bits
  useEffect(() => {
    setSelectionBoxClassNames(
      cx('dropdown-v2__selection-box zola-ui v2-render-field', {
        expanded,
        'has-error': errorMessage,
        'is-disabled': disabled,
        'no-option-selected':
          !selectedOption || (nullable && selectedOption && selectedOption.value === null),
      })
    );
  }, [disabled, errorMessage, expanded, nullable, selectedOption]);

  const [modifiedOptions, setModifiedOptions] = useState([]);

  // Adds the placeholder to the expanded dropdown options if dropdown can be null
  useEffect(() => {
    const labelText = nullOptionLabel || placeholder;
    setModifiedOptions(
      nullable ? [{ label: labelText, value: null, placeholder: true }, ...options] : options
    );
  }, [nullable, options, placeholder, nullOptionLabel]);

  // if value is forcibly changed, select the option with new value
  useEffect(() => {
    if (value !== prevValue && value) {
      const foundOption = modifiedOptions.filter((opt) => opt.value === value)[0];
      setSelectedOption(foundOption);
      if (foundOption) {
        setPrevValue(value);
      }
    }
  }, [modifiedOptions, prevValue, selectedOption, value]);

  // This is used to give each element in the dropdown a unique Id to facilite
  // scrolling to the element when the dropdown is expanded.  There may be
  // other ways to do this.
  const [idPrefix, setIdPrefix] = useState(null);
  useEffect(() => {
    // This shouldn't be needed, id is a required prop, but if its not provided
    // we'll still generate an id for all the dropdown options so they can be
    // scrolled
    setIdPrefix(uuidv4());
  }, []);

  useEffect(() => {
    if (id) {
      setIdPrefix(id);
    }
  }, [id]);

  // if an option is selected and it's not in full view of the user while the dropdown is expanded, scroll to the newly selected option
  useEffect(() => {
    if (!selectedOption) return;
    const currentIndex = _findIndex(options, (x) => x.value === selectedOption.value);
    if (
      (currentIndex || currentIndex === 0) &&
      expanded &&
      document.getElementById(`${idPrefix}-${currentIndex}`)
    ) {
      scrollParentToChild(
        ref.current.getElementsByClassName('dropdown-v2__options')[0],
        document.getElementById(`${idPrefix}-${currentIndex}`)
      );
    }
  }, [selectedOption, expanded, setSelectedOption, options, idPrefix]);

  // handle all key presses required by WAI-ARIA standards
  const handleKeyDown = (e) => {
    // helper function for finding the index of the current option.
    const findCurrentIndex = () => {
      return _findIndex(options, (x) => {
        if (!selectedOption) {
          return false;
        }
        return x.value === selectedOption.value;
      });
    };
    // If ESC, close the dropdown and move focus to next element
    if (e.key === 'Escape') {
      e.preventDefault();
      setExpanded(false);
      const { form } = e.target;
      const index = Array.prototype.indexOf.call(form, e.target);
      form.elements[index + 1].focus();
      e.preventDefault();
    }
    if (e.key === 'Tab') {
      const currentIndex = findCurrentIndex();
      // DON'T prevent default here, we want
      // the tab key to move the user to the next
      // form element!
      setExpanded(false);
      setSelectedOption(options[currentIndex]);
      handleOptionSelection(selectedOption, false);
    }
    // If HOME, open dropdown and move to first element
    if (e.key === 'Home') {
      e.preventDefault();
      setExpanded(true);
      setSelectedOption(options[0]);
    }
    // If END, open dropdown and move to last element
    if (e.key === 'End') {
      e.preventDefault();
      setExpanded(true);
      setSelectedOption(options[options.length - 1]);
    }
    // If UP:
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      // if no option has been selected, select last option
      if (!selectedOption || selectedOption.value === null) {
        setSelectedOption(options[options.length - 1]);
      }
      // If closed, open
      if (!expanded) {
        setExpanded(true);
        return;
      }
      // Otherwise, move one option up. If at the top of the list, circle back to the bottom
      const currentIndex = findCurrentIndex();
      if (currentIndex === 0) {
        setSelectedOption(options[options.length - 1]);
        return;
      }
      setSelectedOption(options[Math.max(currentIndex - 1, 0)]);
      return;
    }
    // If DOWN
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      // If no selected option, select the first option
      if (!selectedOption || selectedOption.value === null) {
        setSelectedOption(options[0]);
      }
      // If closed, open
      if (!expanded) {
        setExpanded(true);
        return;
      }

      // Otherwise, move one option down. If at the bottom of the list, circle back to the top
      const currentIndex = findCurrentIndex();
      if (currentIndex === options.length - 1) {
        setSelectedOption(options[0]);
        return;
      }
      setSelectedOption(options[Math.min(currentIndex + 1, options.length - 1)]);
      return;
    }
    // I need a better test here probably, but this seemed like a good first pass
    // If the name of the key is only one letter long, treat it as search input
    if (e.key.length === 1) {
      e.preventDefault();
      setSearch(search + e.key);
      debouncedClear(); // clear the search string if it's been longer than 0.25sec
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // enter key toggles the dropdown open and closed
      handleOptionSelection(selectedOption, !expanded);
    }
  };

  return (
    <div ref={ref} className={classNames}>
      {/* Label */}
      {label && (
        <label className="dropdown-v2__label" htmlFor={id}>
          {label}
        </label>
      )}
      {isRequired && <span className="text-danger danger-asterisk"> *</span>}
      <div className="input-field__container">
        {/* Input Box */}
        <div
          className={selectionBoxClassNames}
          onClick={() => !disabled && setExpanded(!expanded)}
          id={id}
          tabIndex={0} //eslint-disable-line
          onKeyDown={handleKeyDown}
        >
          <span className="dropdown-v2__selection-box-label">
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <span className="dropdown-v2__selection-box-icon zolaicon zola-ui-icon-chevron-right-bold" />
        </div>
        {/* Options */}
        {options.length > 0 && expanded && (
          <div className="dropdown-v2__options">
            {/* Mobile header for full-screen view */}
            <div className="dropdown-v2__options-mobile-header">
              <div>Select One</div>
              <div
                className="options-header-icon zolaicon zola-ui-icon-close"
                onClick={() => setExpanded(false)}
              />
            </div>
            {/* Actual options */}
            <ul className="dropdown-v2__options-wrapper">
              {modifiedOptions.map((opt, index) => (
                <li
                  key={opt.value}
                  style={useOptionStyles && opt?.style}
                  className={cx('dropdown-v2__option', {
                    'dropdown-v2__option-selected':
                      selectedOption && selectedOption.value === opt.value && !opt.placeholder,
                    'dropdown-v2__option-placeholder': opt.placeholder,
                  })}
                  id={`${idPrefix}-${index}`}
                  onClick={() => {
                    handleOptionSelection(opt, false);
                    setExpanded(false);
                  }}
                >
                  {opt.label}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      {errorMessage && <Error message={errorMessage} iconSize={12} />}
    </div>
  );
};

DropdownV2.defaultProps = {
  nullable: false,
  inlineLabel: false,
  inline: false,
  errorMessage: '',
  placeholder: 'Please select',
};

const OptionProp = PropTypes.shape({
  label: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.number,
    PropTypes.string,
    PropTypes.shape({}),
  ]),
});

const StyledOptionProp = PropTypes.shape({
  label: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.number,
    PropTypes.string,
    PropTypes.shape({}),
  ]),
  style: PropTypes.shape({}),
});

DropdownV2.propTypes = {
  /** Any external effect you want to happen when the user selects an item */
  onSelect: PropTypes.func,
  /** The items in the dropdown */
  options: PropTypes.arrayOf(PropTypes.oneOfType([OptionProp, StyledOptionProp])).isRequired,
  /** The form label that appears above the select box */
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** The text that goes in the input box by default */
  placeholder: PropTypes.string,
  /** Make the whole dropdown an inline element, instead of a block */
  inline: PropTypes.bool,
  /** The text to be displayed in the error message. If it doesn't evaluate to false, the component will be in an error state. */
  errorMessage: PropTypes.string,
  /** A standard class string for the dropdown's outermost div */
  className: PropTypes.string,
  /** Place the label to the side of the dropdown instead of over it */
  inlineLabel: PropTypes.bool,
  /** If the option contains 'style' property, it allows to style each item in dropdown */
  useOptionStyles: PropTypes.bool,
  /** Allow user to select a null-value option with the text from the placeholder */
  nullable: PropTypes.bool,
  /**
   * Alternative text (different from the placeholder) that goes in the options list
   * when the dropdown is nullable.  If not provided, will default to the placeholder.
   */
  nullOptionLabel: PropTypes.string,

  isRequired: PropTypes.bool,
  value: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.number,
    PropTypes.string,
    PropTypes.shape({}),
  ]),
  id: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
};

export default DropdownV2;
